import { c as _c } from "react/compiler-runtime";
import type { IBreadcrumbItem } from '@/components/Molecules/Breadcrumb/Breadcrumb';
import { HeadMeta } from '@/components/Molecules/HeadMeta/HeadMeta';
import { RelatedSidebar, sidebarContentCardRenderer } from '@/components/Molecules/RelatedSidebar/RelatedSidebar';
import { ArticlePageHeader } from '@/components/Organisms/ArticlePageHeader/ArticlePageHeader';
import { ContentTranslation } from '@/components/Organisms/ContentTranslation/ContentTranslation';
import { MachineTranslationDisclaimer } from '@/components/Organisms/ContentTranslation/MachineTranslationDisclaimer';
import { Contributors } from '@/components/Organisms/Contributors/Contributors';
import { RSFViewer } from '@/components/Organisms/RSFViewer/RSFViewer';
import { ArticlePageAside, ArticlePageMain, ArticlePageTemplate } from '@/components/Templates/ArticlePageTemplate/ArticlePageTemplate';
import { InfiniteScrollPage } from '@/components/Templates/InfiniteScrollPage/InfiniteScrollPage';
import type { IPageItemFetcher, IPageRendererProps } from '@/components/Templates/InfiniteScrollPage/ItemPageRenderer';
import { SailthruTracking } from '@/hooks/useSailthruTracking';
import useTranslation from '@/hooks/useTranslation';
import { useUserContentViewTracker } from '@/hooks/useUserContentViewTracker';
import type { IContentItem, IFormChoice, IShortDetail } from '@/interfaces';
import { ContentType } from '@/interfaces';
import { TrackedProductTypes } from '@/services/Analytics/bnefAnalyticsSettings';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import { getAllResearchQuery, SearchContentType } from '@/services/Search';
import type { Translate } from 'next-translate';
import type { FC } from 'react';
import { useId } from 'react';
import { useShortsActions } from './useShortsActions';
interface IShortsItemProps {
  short: IShortDetail;
  relatedStories: IContentItem[];
  translationLocales?: IFormChoice[];
}
export const shortsBreadcrumbs = (t: Translate): IBreadcrumbItem[] => [{
  url: '/shorts',
  label: t('common:Sections.shorts')
}];
const resultsBuilder = async (initialItem: IContentItem, query?: Record<string, any>): Promise<IContentItem[]> => {
  const resultItems: IContentItem[] = [];
  try {
    const searchResults = await new BrowserHttpClient().search({
      path: '/research_search',
      body: getAllResearchQuery(SearchContentType.Short, query)
    });
    if (searchResults?.items) {
      searchResults.items.forEach((item: IContentItem) => {
        if (item.id.toString() !== initialItem.id) {
          resultItems.push(item);
        }
      });
    }
    return resultItems;
  } catch (e) {
    console.warn('Failed to fetch from /research_search: ' + e.message);
    throw e;
  }
};
export const ShortsPageAnalyticsInfo = {
  pageName: 'BNEF: Shorts: Content View',
  siteSection: 'BNEF: Shorts',
  type: TrackedProductTypes.Short
};
export const shortsPageFetcher: IPageItemFetcher<IShortsItemProps> = async (id: string) => {
  const pageData = await new BrowserHttpClient().get({
    path: '/shorts/[ID]',
    replacements: {
      id: id.toLowerCase()
    }
  });
  if (pageData?.short) {
    SailthruTracking.saveTags(id, pageData.short.type, pageData.short.tags);
    return {
      pageData: pageData as IShortsItemProps
    };
  } else if (pageData?.canonicalResource) {
    return {
      canonicalResource: pageData.canonicalResource
    };
  }
  return null;
};
export const ShortsPageRenderer = t0 => {
  const $ = _c(41);
  const {
    pageData,
    isActive
  } = t0;
  useUserContentViewTracker(pageData.short.id, ContentType.short, isActive);
  const {
    t
  } = useTranslation();
  const actions = useShortsActions(pageData.short);
  const disclaimerId = useId();
  const t1 = pageData.short.title;
  const t2 = pageData.short.displayDate;
  let t3;
  if ($[0] !== t) {
    t3 = shortsBreadcrumbs(t);
    $[0] = t;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== actions || $[3] !== pageData.short.displayDate || $[4] !== pageData.short.title || $[5] !== t3) {
    t4 = <ArticlePageHeader title={t1} date={t2} breadCrumbs={t3} actions={actions} />;
    $[2] = actions;
    $[3] = pageData.short.displayDate;
    $[4] = pageData.short.title;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = pageData.short?.translationDetails ?? null;
  let t6;
  if ($[7] !== pageData.translationLocales) {
    t6 = pageData.translationLocales ?? [];
    $[7] = pageData.translationLocales;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== disclaimerId || $[10] !== pageData.short.originalLanguage || $[11] !== pageData.short.url || $[12] !== t5 || $[13] !== t6) {
    t7 = <ContentTranslation originalContentLanguage={pageData.short.originalLanguage} targetDestination={pageData.short.url} currentTranslationDetails={t5} languageOptions={t6} disclaimerId={disclaimerId} />;
    $[9] = disclaimerId;
    $[10] = pageData.short.originalLanguage;
    $[11] = pageData.short.url;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== pageData.short.body) {
    t8 = <RSFViewer htmlContent={pageData.short.body} />;
    $[15] = pageData.short.body;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = pageData.short?.translationDetails;
  let t10;
  if ($[17] !== disclaimerId || $[18] !== t9) {
    t10 = <MachineTranslationDisclaimer id={disclaimerId} translationDetails={t9} />;
    $[17] = disclaimerId;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t7 || $[22] !== t8) {
    t11 = <ArticlePageMain>{t7}{t8}{t10}</ArticlePageMain>;
    $[20] = t10;
    $[21] = t7;
    $[22] = t8;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== pageData.relatedStories || $[25] !== pageData.short.id || $[26] !== t) {
    t12 = pageData.relatedStories.length > 0 && {
      title: t("Sections.related"),
      items: pageData.relatedStories,
      itemRenderer: sidebarContentCardRenderer(`Related: Automated: Short ${pageData.short.id}`)
    };
    $[24] = pageData.relatedStories;
    $[25] = pageData.short.id;
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12) {
    t13 = <RelatedSidebar lists={[t12]} />;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== pageData.short.byPeople) {
    t14 = pageData.short.byPeople?.length > 0 && <Contributors items={pageData.short.byPeople} />;
    $[30] = pageData.short.byPeople;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t13 || $[33] !== t14) {
    t15 = <ArticlePageAside>{t13}{t14}</ArticlePageAside>;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t11 || $[36] !== t15) {
    t16 = <ArticlePageTemplate>{t11}{t15}</ArticlePageTemplate>;
    $[35] = t11;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16 || $[39] !== t4) {
    t17 = <>{t4}{t16}</>;
    $[38] = t16;
    $[39] = t4;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
};
export const ShortsViewPage: FC<IShortsItemProps> = initialData => {
  const $ = _c(9);
  let t0;
  if ($[0] !== initialData.short.leadImage.baseUrl || $[1] !== initialData.short.tags || $[2] !== initialData.short.title) {
    t0 = <HeadMeta title={initialData.short.title} type={ContentType.short} image={initialData.short.leadImage.baseUrl} tags={initialData.short.tags} />;
    $[0] = initialData.short.leadImage.baseUrl;
    $[1] = initialData.short.tags;
    $[2] = initialData.short.title;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== initialData) {
    t1 = <InfiniteScrollPage initialPageData={initialData} resultsBuilder={resultsBuilder} currentItem={initialData.short} />;
    $[4] = initialData;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t0 || $[7] !== t1) {
    t2 = <>{t0}{t1}</>;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};